import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useGlobalStore } from '../../stores/globalStore';
import { resolvePageTitle } from '../../utils/pageTitles';

/**
 * Says which page you just arrived on.
 *
 *
 * WHY
 *
 * In a multi-page site the browser announces each new page: it reads the
 * title, resets focus to the top, and the reader knows where they are. A
 * single-page app gets none of that for free. React Router swaps the DOM
 * without a navigation, so nothing is announced, `document.title` keeps
 * whatever it was, and focus stays wherever it was left.
 *
 * Measured on this app before this existed — activating "Reports" in the nav
 * rail produced, in full:
 *
 *     menuitem "Reports"                        (the item she just activated)
 *     Data grid with 40 rows and 7 columns      (x3, from DevExtreme)
 *
 * She is never told she is on Reports. The title stayed "Dietech Software" on
 * all 191 routes, and focus stayed on the nav item, so the next Tab walked the
 * rest of the nav rail rather than entering the page she had just opened.
 *
 *
 * WHAT IT SAYS
 *
 * On ARRIVAL IN THE APP, once per session: where you are, and how to get
 * anywhere else. Naming the page alone answers "where am I" and leaves
 * "what do I do now" unanswered — and someone who cannot see the nav rail has
 * no way to discover that a command menu exists, or that it can be driven by
 * pressing a number. An interface that has to be seen to be learned is not
 * usable by the person this was built for.
 *
 * Both commands in the message were checked against the running app rather
 * than assumed. Shift+? is deliberately NOT offered: that binding was removed
 * (see useKeyboardShortcuts.ts), and sending her to a dead key is worse than
 * saying nothing. F1 was added for this, because the keyboard reference was
 * previously reachable only through Help -> About -> Keyboard Shortcuts —
 * three levels of a menu you must be able to see, to reach the list of ways to
 * avoid needing to see it.
 *
 * On EVERY LATER NAVIGATION: the page name alone. The orientation is worth
 * sitting through once; on every navigation it would be an obstacle, and by
 * then she knows it.
 *
 * `document.title` gets the same name so the browser, the tab, and the window
 * switcher agree with what was spoken.
 *
 * Pages that can say something more specific than their route should: a
 * resident record is more useful announced by name than as "Resident Details".
 * `usePageAnnouncement` is the hook for that.
 *
 *
 * FOCUS
 *
 * Moved to <main>, which already carries tabIndex={-1} as the skip link's
 * target. That is the smallest correct move: it puts her at the top of the new
 * content, so Tab continues into the page instead of resuming in the nav rail,
 * and it is where the skip link would have taken her anyway.
 *
 * Not done on first load. That IS a real browser navigation, so the browser
 * has already announced the document and moved focus; doing it again would
 * both duplicate the announcement and skip her past the "Skip to main content"
 * link before she could reach it.
 */

/**
 * Spoken once when the app is first opened.
 *
 * Written out in words — "Control plus Slash" rather than "Ctrl+/" — because
 * screen readers skip or mangle punctuation depending on the user's symbol
 * level, and an instruction that reads as "control slash" is one she cannot
 * act on.
 */
const ORIENTATION = (page: string) =>
  `Dietech. ${page}. Press Control plus Slash to open the command menu: it lists what you can `
  + 'do on this screen and every page you can reach, and you can press a number from one to '
  + 'nine to pick one. '
  + 'Press F1 for the keyboard guide. Press Tab to move through this page.';

/**
 * What a page says about itself, beyond what its route can know.
 *
 * `title` names it — "Smith, John" rather than "Resident Details" — and is
 * what the tab shows. `detail` is spoken only, and is the answer to "what is
 * on this page and what can I do here".
 *
 * That second half is the point. A name tells her where she landed and leaves
 * her to discover the rest by tabbing through it, which on a page of 93
 * reports means hearing 93 things to find out what the page contains. Sighted
 * users get that from a glance at the category chips; `detail` is the spoken
 * equivalent.
 *
 * Kept OUT of the title deliberately: a tab reading "Reports. 93 reports in 6
 * categories..." is useless in a window switcher.
 */
export interface PageAnnouncement {
  title?: string;
  detail?: string;
}

let pageOverride: (PageAnnouncement & { path: string }) | null = null;
const overrideListeners = new Set<() => void>();

/**
 * Lets a page describe itself. Pass null while the data it needs is still
 * loading; the route-derived name is used until it arrives.
 */
export function setPageAnnouncement(value: PageAnnouncement | string | null): void {
  // Stamped with the path it describes. React runs CHILD effects before parent
  // ones, so a page setting its description and PageAnnouncer clearing the
  // previous page's both happen on the same commit — and clearing ran second,
  // wiping the description that had just been set. Every report page announced
  // as the bare word "Reports" because of it.
  //
  // Stamping removes the ordering question: an override is used only on the
  // path it was set for, so a stale one is ignored rather than raced against.
  const next = typeof value === 'string' ? { title: value } : value;
  pageOverride = next ? { ...next, path: window.location.pathname } : null;
  overrideListeners.forEach((fn) => fn());
}

/**
 * Announces what this page is and what can be done on it.
 *
 * Pass an empty detail while loading rather than a wrong one — "0 reports"
 * spoken confidently is worse than a moment's silence.
 */
export function usePageAnnouncement(value: PageAnnouncement | null): void {
  const key = `${value?.title ?? ''}|${value?.detail ?? ''}`;
  useEffect(() => {
    if (!value?.title && !value?.detail) return;
    setPageAnnouncement(value);
    // Not cleared here: PageAnnouncer drops the override on the next route
    // change, so a name can never outlive its page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/**
 * Puts focus at the top of the newly-opened page — but only when it would
 * otherwise be stranded.
 *
 * Without any move, focus stays on the nav item she activated, so the next Tab
 * walks the rest of the nav rail instead of entering the page she just opened.
 *
 * Moving it unconditionally is worse. The command palette navigates while it
 * is still open and still holding focus; stealing focus mid-flight sent her
 * keystrokes nowhere, so typing found no results and pressing a number did not
 * navigate. That surfaced as two intermittently failing palette tests — the
 * kind of race that passes locally and fails on a slower CI machine, which is
 * exactly what happened.
 *
 * So: never take focus from anything that deliberately holds it. A dialog
 * traps focus by design, and a page that focuses its own search box has chosen
 * a landing spot better than this one.
 */
export function moveFocusIntoPage(): void {
  // Guarded: the layout is absent on the standalone routes (login, kiosk,
  // self-order).
  const main = document.getElementById('main-content');
  if (!main) return;

  const active = document.activeElement as HTMLElement | null;

  // A dialog, menu or listbox owns its focus for as long as it is open.
  if (active?.closest('[role="dialog"], [role="menu"], [role="listbox"], [aria-modal="true"]')) return;

  // Already inside the page — a page that focused its own field wins.
  if (active && active !== document.body && main.contains(active)) return;

  main.focus({ preventScroll: true });
}

export default function PageAnnouncer() {
  const location = useLocation();
  const residentLabel = useGlobalStore((s) => s.residentLabel);
  const residentPluralLabel = useGlobalStore((s) => s.residentPluralLabel);
  const facilityName = useGlobalStore((s) => s.facilityName);

  const [message, setMessage] = useState('');

  // What was last actually SPOKEN — flipped inside the timer callback, never
  // when one is scheduled. Two bugs came from getting this wrong:
  //
  //  1. An isFirstRender ref lost the orientation entirely. The facility
  //     labels arrive asynchronously, so the effect re-ran with the flag
  //     already cleared and overwrote the pending orientation with the bare
  //     page name.
  //
  //  2. Marking it on SCHEDULE lost it again, to StrictMode's double mount:
  //     the cleanup cancelled the timer, the ref survived the remount, and the
  //     second mount saw the work as already done. This repo has that gotcha
  //     written down — use a cancelled flag, not a useRef guard — and a ref
  //     whose lifetime does not match its timer's is the same mistake wearing
  //     a different hat.
  //
  // Recording it only once the announcement really happens means a torn-down
  // effect leaves no trace, and the remount does the work.
  const announcedPath = useRef<string | null>(null);
  const announcedName = useRef<string | null>(null);
  // Bumped when a page supplies its own description. It has to be a real
  // dependency of the announcing effect below, not just a re-render trigger:
  // a page sets its description AFTER its data loads, long after the route
  // changed, so without this the effect never re-reads the override and every
  // page announced as its bare route name.
  const [overrideTick, forceUpdate] = useState(0);

  // Re-run when a page supplies its own name after its data loads.
  useEffect(() => {
    const fn = () => forceUpdate((n) => n + 1);
    overrideListeners.add(fn);
    return () => { overrideListeners.delete(fn); };
  }, []);

  useEffect(() => {
    const routeName = resolvePageTitle(location.pathname, {
      resident: residentLabel,
      residentPlural: residentPluralLabel,
    });
    // Only honoured on the page it was written for.
    const override = pageOverride?.path === location.pathname ? pageOverride : null;
    const name = override?.title || routeName;
    const spoken = override?.detail ? `${name}. ${override.detail}` : name;

    // The tab gets the NAME only; the detail is for listening, not reading.
    //
    // The facility leads, ahead of the page, because this string is also how
    // two windows are told apart in the taskbar and the window switcher --
    // and both truncate from the END. Support and corporate users routinely
    // have two facilities open (see utils/facilityWindow.ts); "Riverside..."
    // answers which window is which, "Residents..." does not.
    document.title = facilityName
      ? `${facilityName} · ${name} · Dietech`
      : `${name} · Dietech`;

    // Already said this, for this page. A re-render for any other reason —
    // labels loading, a parent updating — must not talk over her.
    if (announcedPath.current === location.pathname && announcedName.current === spoken) return;

    const isArrival = announcedPath.current === null;

    if (!isArrival) moveFocusIntoPage();

    // Blanked first: aria-live fires on content CHANGE, so navigating from a
    // resident back to the same resident would otherwise be the same string
    // twice and never spoken the second time.
    setMessage('');

    let cancelled = false;
    const id = setTimeout(() => {
      if (cancelled) return;
      announcedPath.current = location.pathname;
      announcedName.current = spoken;
      setMessage(isArrival ? ORIENTATION(spoken) : spoken);
      // The orientation is held back so it queues behind the browser's own
      // announcement of the document rather than racing it.
    }, isArrival ? 900 : 80);

    return () => { cancelled = true; clearTimeout(id); };
  }, [location.pathname, residentLabel, residentPluralLabel, facilityName, overrideTick]);

  return (
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {message}
    </div>
  );
}
